import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { ShieldBan, ShieldQuestion, FileText, Search, CircleHelp } from "lucide-react";
import { api as convexApi } from "../../lib/convexApi";

// The DLP scanner ships 52 patterns and `listPatternMetadata` has existed —
// security-gated, zero-arg, returning a flat array (convex/dlp/queries.ts:59)
// — with no caller anywhere in the repo. The Security
// Room's DLP page shows only what has already FIRED, so an org with no hits saw
// an empty screen and no way to learn what is being looked for. "Nothing has
// leaked" and "nothing is being checked" rendered identically. This tab is the
// catalog: what the scanner detects, what happens when it does, and — since the
// per-org opt-out list landed — which of them this organisation has switched
// off.

type PatternTier = "high" | "medium" | "low";

interface PatternMeta {
  id: string;
  label: string;
  tier: PatternTier;
  enabled: boolean;
}

// From shared-lib/src/dlpScan.ts `dlpTierForDecision` — the tier IS the verdict,
// so naming the verdict is the whole point of showing the tier. An operator
// reading "high" should not have to go find out that it means the action is
// blocked outright.
const TIERS: Array<{
  tier: PatternTier;
  verdict: string;
  blurb: string;
  icon: typeof ShieldBan;
}> = [
  {
    tier: "high",
    verdict: "deny",
    blurb: "Blocked outright. The command or message does not run.",
    icon: ShieldBan,
  },
  {
    tier: "medium",
    verdict: "ask",
    blurb: "Held for approval. Someone has to confirm before it proceeds.",
    icon: ShieldQuestion,
  },
  {
    tier: "low",
    verdict: "log",
    blurb: "Recorded and allowed. Visible in the audit trail, never blocked.",
    icon: FileText,
  },
];

// What the org gives up by switching a pattern off, in the terms the tier
// header already uses. The confirm has to name the protection being dropped —
// "are you sure?" over a pattern id tells an admin nothing about whether the
// thing they are about to stop detecting was blocking secrets or writing log
// lines.
function consequenceForTier(tier: string): string {
  if (tier === "high") return "Matches will no longer be blocked.";
  if (tier === "medium") return "Matches will no longer be held for approval.";
  if (tier === "low") return "Matches will no longer be recorded.";
  // Unrecognised tier: this build cannot say which verdict is being lost, so it
  // must not guess one. See the `other` bucket below for the same reasoning.
  return "Matches will no longer be flagged.";
}

export function DlpPatternsTab() {
  const patterns = useQuery((convexApi as any).dlp.queries.listPatternMetadata, {}) as
    | Array<Partial<PatternMeta>>
    | undefined;
  // `myRole` is gate-free (requireOrg with no plan arg — convex/billing/
  // queries.ts:62), so it resolves for anyone who can already see this tab.
  const myRole = useQuery((convexApi as any).billing.queries.myRole, {}) as
    | { isAdmin?: boolean }
    | undefined;
  const setPatternEnabled = useMutation((convexApi as any).dlp.mutations.setPatternEnabled);

  const [q, setQ] = useState("");
  // A SET, not a single id. An admin switching several patterns off in a row
  // has more than one write in flight, and a single slot tracks only the most
  // recent — the earlier rows unlock mid-write and can be double-submitted.
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(() => new Set());
  // Keyed by pattern id for the same reason `busyIds` is a Set, and it has to
  // agree with it: with concurrent writes supported, a single error slot means
  // the second row's failure silently overwrites the first's, and merely
  // STARTING a second toggle wipes a failure the operator has not read yet.
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(() => new Map());

  // The mutation is admin-only server-side (convex/dlp/mutations.ts:34). Gating
  // the control too turns a 403 an operator cannot act on into a locked switch
  // that explains itself.
  //
  // `=== true`, not `!== false`: the query resolving to a shape without a
  // boolean `isAdmin` — an older deployment, a payload change — must read as
  // "not an admin". Anything other than an explicit yes leaves the switch
  // locked, which costs an admin one refresh and costs a non-admin nothing.
  // (The still-loading case is handled separately by `lockReason` below; this
  // is only about a RESOLVED but unexpected value.)
  const isOrgAdmin = myRole?.isAdmin === true;

  // Why a reason string rather than a boolean: the two locked states are not
  // the same claim. `patterns` and `myRole` resolve independently, so an admin
  // whose catalog arrives first would be told "only organisation admins can
  // change this" for the frames before their own role lands — a false
  // statement, and precisely the class of copy this tab exists to eliminate.
  // The switch stays locked either way (`aria-disabled`, see the button — NOT
  // `disabled`, which would eat the hover that shows this very string); only
  // the explanation differs.
  const lockReason: string | null =
    myRole === undefined
      ? "Checking your permissions…"
      : isOrgAdmin
        ? null
        : "Only organisation admins can change this";

  // Structural validity only — NOT the search. The header counts these, so the
  // number must not move as someone types. Both fields are checked: a pattern
  // with an id but no label is unsearchable and renders as a blank row, and
  // validating only the id is what let `p.label.toLowerCase()` crash the tab.
  //
  // `enabled` is normalised rather than required. It is absent only against a
  // deployment that predates the opt-out list — a backend with no way to
  // disable anything, where every pattern really is on — so treating a missing
  // value as `true` reports that skew correctly instead of blanking the tab.
  const valid: PatternMeta[] = useMemo(
    () =>
      // `Array.isArray`, not `?? []`: a non-array payload has no `.filter`, so
      // the nullish default only covers null/undefined and a malformed
      // response takes down the whole admin console with a TypeError during
      // render. Degrading to an empty list routes it to the "nothing is being
      // scanned for" alarm below — which is the correct thing to say when the
      // catalog cannot be read.
      (Array.isArray(patterns) ? patterns : [])
        .filter(
          (p): p is Partial<PatternMeta> =>
            Boolean(p) && typeof p.id === "string" && typeof p.label === "string"
        )
        .map((p) => ({
          id: p.id as string,
          label: p.label as string,
          tier: p.tier as PatternTier,
          enabled: p.enabled !== false,
        })),
    [patterns]
  );

  const activeCount = useMemo(() => valid.filter((p) => p.enabled).length, [valid]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return valid;
    // Match the id too: an operator chasing a specific audit row has the
    // pattern id in front of them, not its prose label.
    return valid.filter(
      (p) => p.label.toLowerCase().includes(needle) || p.id.toLowerCase().includes(needle)
    );
  }, [valid, q]);

  // Anything whose tier is not one of the three known ones goes to `other`
  // rather than being skipped. `valid` already counted it in the header, so
  // dropping it here would print "5 patterns are active" above a list of 4 —
  // and the missing one is, by definition, the pattern nobody understands.
  const { byTier, other } = useMemo(() => {
    const out: Record<PatternTier, PatternMeta[]> = { high: [], medium: [], low: [] };
    const rest: PatternMeta[] = [];
    for (const p of filtered) {
      // `Array.isArray`, not a truthiness check: `out["toString"]` resolves up
      // the prototype chain to a truthy function, so a pattern with tier
      // "toString" or "constructor" would take the known-tier branch and throw
      // on `.push`. The `other` bucket exists to survive an unexpected tier;
      // crashing on one particular flavour of unexpected would defeat it.
      if (Array.isArray(out[p.tier])) out[p.tier].push(p);
      else rest.push(p);
    }
    return { byTier: out, other: rest };
  }, [filtered]);

  async function onToggle(p: PatternMeta, locked: string | null) {
    // THIS is the lock, not the attribute on the button. The permission lock
    // renders as `aria-disabled` rather than `disabled` so its explanation is
    // reachable (see the button), which means a click can genuinely arrive
    // here. Re-checking `busyIds` too keeps the whole invariant stated once.
    //
    // `locked` is passed in rather than closed over so it is THE SAME VALUE
    // the button rendered from. Reading the parent's `lockReason` here instead
    // let a section rendered with its own lock still fire the mutation — the
    // control looked locked and wasn't.
    if (locked !== null || busyIds.has(p.id)) return;

    // Confirm on the way OFF only. Switching detection back on restores a
    // protection; there is nothing to warn about, and a prompt there would
    // train operators to click through the one that matters.
    if (
      p.enabled &&
      !window.confirm(
        `Stop detecting “${p.label}” (${p.id})?\n\n` +
          `${consequenceForTier(p.tier)} This applies to the whole organisation, ` +
          `not just you.\n\nThe change is recorded in the audit trail. You can switch ` +
          `it back on here at any time.`
      )
    ) {
      return;
    }
    setBusyIds((cur) => new Set(cur).add(p.id));
    // Clear only THIS row's stale error. Clearing all of them would erase a
    // sibling row's unread failure just because a different toggle was
    // started.
    setErrors((cur) => {
      if (!cur.has(p.id)) return cur;
      const next = new Map(cur);
      next.delete(p.id);
      return next;
    });
    try {
      await setPatternEnabled({ patternId: p.id, enabled: !p.enabled });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setErrors((cur) => new Map(cur).set(p.id, message));
    } finally {
      setBusyIds((cur) => {
        const next = new Set(cur);
        next.delete(p.id);
        return next;
      });
    }
  }

  // `== null`, not `!patterns`: an empty ARRAY is a real answer — it means the
  // scanner has no patterns, which is the single most alarming thing this tab
  // can report. Treating it as "still loading" would hide exactly that.
  if (patterns == null) {
    return <p className="muted-fg">Loading detection patterns…</p>;
  }

  const offCount = valid.length - activeCount;

  return (
    <div className="dlp-catalog">
      <header className="dlp-catalog__head">
        <div>
          <h2>Detection patterns</h2>
          {/* An empty catalog gets no count sentence at all. "All 0 patterns
              are active" printed directly above "Nothing is being scanned for"
              reads as reassurance stapled to an alarm. The guard sits OUTSIDE
              the <p>, not inside it: returning null from within still renders
              `<p class="muted-fg"></p>`, an empty block whose margin pushes the
              alarm down the page for no reason. */}
          {valid.length > 0 && (
            <p className="muted-fg">
              {offCount === 0 ? (
                <>
                  All {valid.length} {valid.length === 1 ? "pattern is" : "patterns are"} active
                  across every scanned surface — chat messages, terminal commands, memory
                  observations, and pull-request diffs.
                </>
              ) : (
                <>
                  {activeCount} of {valid.length} patterns are active.{" "}
                  <strong className="dlp-catalog__off-count">
                    {offCount} switched off for this organisation
                  </strong>{" "}
                  — those go undetected on every surface, for every member.
                </>
              )}
            </p>
          )}
          {/* Naming the surfaces this control does NOT reach is not a caveat
              for the changelog: an admin who switches a pattern off and then
              watches a scan still redact it would reasonably conclude the
              control is broken. Update this sentence in the same PR that
              migrates a path — a stale claim here is a security-relevant lie,
              in whichever direction it is stale. */}
          <p className="muted-fg dlp-catalog__scope">
            Opt-outs apply to stored chat messages, live CLI output, terminal commands, memory
            observations — including those served over the MCP memory server — pull-request diffs
            and manual scans.
          </p>
          {myRole !== undefined && !isOrgAdmin && (
            // "change these switches", not "switch a pattern off": a non-admin
            // cannot switch one back ON either, and naming only the off
            // direction implies they could restore a protection they cannot.
            // Understating a restriction is the wrong direction of error on
            // the tab whose job is to say exactly what is being scanned for.
            <p className="muted-fg dlp-catalog__scope">
              Only organisation admins can change these switches.
            </p>
          )}
        </div>
        <label className="dlp-catalog__search">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            value={q}
            placeholder="Filter by name or id"
            aria-label="Filter detection patterns"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </header>

      {filtered.length === 0 &&
        (q.trim() ? (
          <p className="muted-fg">No pattern matches “{q}”.</p>
        ) : (
          // Not a search miss — the scanner reported no patterns at all. That is
          // an alarm, not an empty list, so it must not read like one.
          <p className="muted-fg" role="alert">
            No detection patterns are loaded. Nothing is being scanned for.
          </p>
        ))}

      {TIERS.map(({ tier, verdict, blurb, icon: Icon }) => (
        <TierSection
          key={tier}
          slug={tier}
          heading={`${tier} — ${verdict}`}
          blurb={blurb}
          icon={Icon}
          rows={byTier[tier]}
          lockReason={lockReason}
          busyIds={busyIds}
          errors={errors}
          onToggle={onToggle}
        />
      ))}

      <TierSection
        slug="other"
        heading="unrecognised tier"
        blurb="The scanner reported these under a tier this build does not know, so the verdict they produce cannot be shown. They are listed here rather than dropped, because a pattern nobody can explain is the one worth seeing."
        icon={CircleHelp}
        rows={other}
        lockReason={lockReason}
        busyIds={busyIds}
        errors={errors}
        onToggle={onToggle}
      />
    </div>
  );
}

function TierSection({
  slug,
  heading,
  blurb,
  icon: Icon,
  rows,
  lockReason,
  busyIds,
  errors,
  onToggle,
}: {
  slug: string;
  heading: string;
  blurb: string;
  icon: typeof ShieldBan;
  rows: PatternMeta[];
  lockReason: string | null;
  busyIds: ReadonlySet<string>;
  errors: ReadonlyMap<string, string>;
  // Takes the lock it rendered with, so the guard and the `aria-disabled`
  // attribute can never be computed from two different values.
  onToggle: (p: PatternMeta, locked: string | null) => void;
}) {
  if (rows.length === 0) return null;
  // The count stays the tier TOTAL — it is how many patterns of this severity
  // exist, which does not change when one is switched off. What changes is how
  // many are live, so the off count rides alongside it rather than silently
  // reducing the total: a tier reading "12" with three of them dead is the
  // reading this tab exists to prevent.
  const off = rows.filter((p) => !p.enabled).length;
  return (
    <section className="dlp-catalog__tier">
      <div className={`dlp-catalog__tier-head is-${slug}`}>
        <Icon size={16} aria-hidden="true" />
        <h3>{heading}</h3>
        <span className="dlp-catalog__count">
          {rows.length}
          {off > 0 && <span className="dlp-catalog__off-count"> · {off} off</span>}
        </span>
      </div>
      <p className="muted-fg dlp-catalog__blurb">{blurb}</p>
      <ul className="dlp-catalog__list">
        {rows.map((p) => (
          <li key={p.id} className={`dlp-catalog__item${p.enabled ? "" : " is-off"}`}>
            <span className="dlp-catalog__label">{p.label}</span>
            <code className="dlp-catalog__id">{p.id}</code>
            <button
              type="button"
              role="switch"
              aria-checked={p.enabled}
              aria-label={`Detect ${p.label}`}
              className={`dlp-catalog__toggle${p.enabled ? " is-on" : ""}`}
              // `disabled` only for the transient in-flight state, which needs
              // no explanation. The permission lock uses `aria-disabled`: a
              // disabled form control receives no pointer events, so its
              // `title` never renders — the lockReason below would explain
              // nothing to the one person who needs it. It also keeps the
              // switch in the tab order and announced as unavailable, instead
              // of vanishing from keyboard navigation entirely. `onToggle`
              // does the actual enforcing.
              // Both, and they must agree: React renders `aria-disabled` even
              // when false, so gating it on `lockReason` alone put a literal
              // `aria-disabled="false"` on a natively `disabled` button while
              // a write was in flight — an element telling assistive tech it
              // is available and unavailable in the same breath.
              disabled={busyIds.has(p.id)}
              aria-disabled={lockReason !== null || busyIds.has(p.id)}
              title={
                lockReason ??
                (p.enabled ? "Stop detecting this pattern" : "Resume detecting this pattern")
              }
              onClick={() => onToggle(p, lockReason)}
            >
              {p.enabled ? "On" : "Off"}
            </button>
            {errors.get(p.id) && (
              <span className="dlp-catalog__error" role="alert">
                ⚠ {errors.get(p.id)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
